import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import Modal from "../../../components/base/Modal";
import { buildThemeClasses } from "../theme";
import EntityForm from "./EntityForm";
import { TeamViewToggle } from "./TeamViewToggle";

// ---------------------------------------------------------------------------
// Shell CRUD reutilizable, respaldado por API, para la mini-app Colaboradores y
// Equipo. Adaptado de Layouts/mantenimiento/components/ApiCrudSection.tsx
// (y del homólogo de Compras), añadiendo el toggle grid/list
// (TeamViewToggle): cada tabla ofrece vista de tabla (list) y vista de
// tarjetas (grid). El padre es dueño de las filas y de los handlers async
// create/update/delete. Data-agnóstico, responsive y con dark mode.
// ---------------------------------------------------------------------------

export type Row = Record<string, unknown> & { id: string };

export interface Column {
  key: string;
  label: string;
  align?: "left" | "center" | "right";
  width?: string;
  render?: (row: Row) => React.ReactNode;
}

export interface RepeatColumn {
  key: string;
  label: string;
  type?: "text" | "number" | "select";
  /** Opciones de una columna `select` (p. ej. los colaboradores de un squad). */
  options?: { value: string; label: string }[];
  /** Una columna `select` con `unique` no deja elegir el mismo valor en dos filas. */
  unique?: boolean;
  placeholder?: string;
}

export interface FormField {
  key: string;
  label: string;
  type?: "text" | "number" | "textarea" | "select" | "date" | "repeat";
  required?: boolean;
  placeholder?: string;
  options?: { value: string; label: string }[];
  defaultValue?: string | number;
  repeatColumns?: RepeatColumn[];
  addRowLabel?: string;
  /** Texto de ayuda bajo el campo (p. ej. reglas de una tarifa). */
  hint?: string;
  /** Muestra el campo solo cuando aplica (p. ej. según la modalidad elegida). */
  visibleWhen?: (values: Record<string, string>) => boolean;
  /**
   * Al cambiar ESTE campo, otros valores del formulario que deben seguirlo
   * (p. ej. elegir un rango de sueldo rellena modalidad, moneda y sueldo).
   * Recibe el valor nuevo y el formulario ya con él; devuelve lo que hay que
   * sobreescribir. Lo que devuelve es un rellenado, no un candado: la persona
   * puede corregirlo después a mano.
   */
  derive?: (value: string, values: Record<string, string>) => Record<string, string> | void;
  /**
   * Opciones que dependen de lo ya capturado (p. ej. los rangos de sueldo del
   * puesto elegido). Si está, sustituye a `options`.
   */
  optionsFor?: (values: Record<string, string>) => { value: string; label: string }[];
}

interface ApiCrudSectionProps {
  title: string;
  subtitle?: string;
  icon?: string;
  columns: Column[];
  fields: FormField[];
  rows: Row[];
  loading?: boolean;
  error?: string | null;
  searchKey?: string;
  readOnly?: boolean;
  addLabel?: string;
  toolbar?: React.ReactNode;
  extraActions?: (row: Row) => React.ReactNode;
  /** Tarjeta personalizada de la vista grid; por defecto se deriva de columns. */
  gridCard?: (row: Row) => React.ReactNode;
  /** Pie del formulario del modal (p. ej. vista previa del cálculo del pago). */
  formFooter?: (values: Record<string, string>) => React.ReactNode;
  /** Bloque entre la cabecera y la lista (p. ej. los indicadores, #1595). */
  header?: React.ReactNode;
  /** Panel izquierdo colapsable (p. ej. árbol de áreas y cuadrillas, #1595). */
  sidebar?: React.ReactNode;
  /** Abrir el detalle al elegir una fila o tarjeta (#1595). */
  onRowClick?: (row: Row) => void;
  /** Vista inicial: tabla (por omisión) o tarjetas. */
  defaultView?: "grid" | "list";
  /** Registro abierto desde fuera (p. ej. «Editar» del panel de detalle). */
  editRequest?: Row | null;
  onEditRequestHandled?: () => void;
  onCreate?: (values: Record<string, string>) => Promise<void> | void;
  onUpdate?: (id: string, values: Record<string, string>) => Promise<void> | void;
  onDelete?: (id: string) => Promise<void> | void;
}

export default function ApiCrudSection({
  title,
  subtitle,
  icon,
  columns,
  fields,
  rows,
  loading = false,
  error = null,
  searchKey,
  readOnly = false,
  addLabel,
  toolbar,
  extraActions,
  gridCard,
  formFooter,
  header,
  sidebar,
  onRowClick,
  defaultView = "list",
  editRequest = null,
  onEditRequestHandled,
  onCreate,
  onUpdate,
  onDelete,
}: ApiCrudSectionProps) {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);

  const [query, setQuery] = useState("");
  const [selectedView, setSelectedView] = useState<"grid" | "list">(defaultView);
  const [modal, setModal] = useState<null | { mode: "add" | "edit"; row?: Row }>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    if (!editRequest) return;
    setModal({ mode: "edit", row: editRequest });
    onEditRequestHandled?.();
  }, [editRequest, onEditRequestHandled]);
  const [page, setPage] = useState(0);
  const pageSize = 10;

  const key = searchKey ?? columns[0]?.key;
  const filtered = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.toLowerCase();
    return rows.filter((r) => String(r[key] ?? "").toLowerCase().includes(q));
  }, [rows, query, key]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = filtered.slice(safePage * pageSize, safePage * pageSize + pageSize);

  const closeModal = () => setModal(null);

  const handleSubmit = async (values: Record<string, string>) => {
    if (modal?.mode === "edit" && modal.row) {
      await onUpdate?.(modal.row.id, values);
    } else {
      await onCreate?.(values);
    }
    closeModal();
  };

  const cellAlign = (a?: string) =>
    a === "right" ? "text-right" : a === "center" ? "text-center" : "text-left";

  const hasRowActions = !readOnly || !!extraActions;

  const rowActions = (row: Row) =>
    hasRowActions ? (
      <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
        {extraActions?.(row)}
        {!readOnly && onUpdate && (
          <button
            title={t("team.edit")}
            onClick={() => setModal({ mode: "edit", row })}
            className="rounded-md p-1.5 text-primary-500 hover:bg-primary-500/10"
          >
            <i className="ri-edit-line" />
          </button>
        )}
        {!readOnly && onDelete && (
          <button
            title={t("team.delete")}
            onClick={() => onDelete(row.id)}
            className="rounded-md p-1.5 text-red-500 hover:bg-red-500/10"
          >
            <i className="ri-delete-bin-line" />
          </button>
        )}
      </div>
    ) : null;

  // Tarjeta por defecto de la vista grid, construida desde las columnas.
  const defaultCard = (row: Row) => (
    <div className={`rounded-xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
      <div className="mb-2 flex items-start justify-between gap-2">
        <span className={`font-semibold ${tc.text}`}>
          {columns[0]?.render ? columns[0].render(row) : String(row[columns[0]?.key] ?? "")}
        </span>
        {rowActions(row)}
      </div>
      <dl className="space-y-1">
        {columns.slice(1).map((c) => (
          <div key={c.key} className="flex justify-between gap-2 text-xs">
            <dt className={tc.textSecondary}>{c.label}</dt>
            <dd className={`text-right ${tc.text}`}>
              {c.render ? c.render(row) : String(row[c.key] ?? "")}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className={`mx-auto ${sidebar ? "max-w-7xl" : "max-w-6xl"}`}>
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {icon && (
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
                <i className={`${icon} text-xl`} />
              </span>
            )}
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>{title}</h1>
              {subtitle && <p className={`text-sm ${tc.textSecondary}`}>{subtitle}</p>}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {toolbar}
            <TeamViewToggle
              themeClasses={tc}
              selectedView={selectedView}
              onViewChange={setSelectedView}
            />
            <div
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${tc.border} ${tc.cardBg}`}
            >
              <i className={`ri-search-line ${tc.textSecondary}`} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("team.search")}
                className={`bg-transparent text-sm outline-none ${tc.text}`}
              />
            </div>
            {!readOnly && onCreate && (
              <button
                onClick={() => setModal({ mode: "add" })}
                className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-400"
              >
                <i className="ri-add-line" />
                <span className="whitespace-nowrap">{addLabel ?? t("team.add")}</span>
              </button>
            )}
          </div>
        </div>

        {header && <div className="mb-4">{header}</div>}

        <div className={sidebar ? "flex flex-col gap-4 lg:flex-row lg:items-start" : ""}>
        {sidebar && (
          <aside className={`shrink-0 rounded-2xl border ${tc.cardBorder} ${tc.cardBg} ${sidebarOpen ? "lg:w-60" : "lg:w-11"}`} data-testid="crud-sidebar">
            <button
              type="button"
              onClick={() => setSidebarOpen((v) => !v)}
              aria-expanded={sidebarOpen}
              aria-label={t("staffOps.sidebar.toggle")}
              className={`flex w-full items-center justify-between gap-2 border-b px-3 py-2 text-xs font-semibold ${tc.cardBorder} ${tc.text}`}
            >
              {sidebarOpen && <span>{t("staffOps.sidebar.title")}</span>}
              <i className={sidebarOpen ? "ri-side-bar-fill" : "ri-side-bar-line"} aria-hidden="true" />
            </button>
            {sidebarOpen && <div className="max-h-[60vh] overflow-y-auto p-2">{sidebar}</div>}
          </aside>
        )}
        <div className="min-w-0 flex-1">
        {error && (
          <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-500">
            <i className="ri-error-warning-line mr-1" />
            {error}
          </div>
        )}

        {/* Estados de carga / vacío compartidos por ambas vistas */}
        {loading ? (
          <div
            className={`rounded-2xl border p-8 text-center ${tc.cardBorder} ${tc.cardBg} ${tc.textSecondary}`}
          >
            <i className="ri-loader-4-line animate-spin mr-1" />
            {t("team.loading")}
          </div>
        ) : pageRows.length === 0 ? (
          <div
            className={`rounded-2xl border p-8 text-center ${tc.cardBorder} ${tc.cardBg} ${tc.textSecondary}`}
          >
            {t("team.empty")}
          </div>
        ) : selectedView === "grid" ? (
          /* --- Vista GRID (tarjetas) --- */
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {pageRows.map((row) => (
              <div
                key={row.id}
                data-testid={`crud-card-${row.id}`}
                className={onRowClick ? "cursor-pointer" : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {(gridCard ?? defaultCard)(row)}
              </div>
            ))}
          </div>
        ) : (
          /* --- Vista LIST (tabla) --- */
          <div className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
            <div className="w-full overflow-x-auto">
              <table className="w-full overflow-hidden rounded-xl text-xs shadow-sm">
                <thead className="bg-primary-500 text-white">
                  <tr>
                    {columns.map((c) => (
                      <th
                        key={c.key}
                        style={{ width: c.width }}
                        className={`p-3 font-bold ${cellAlign(c.align)}`}
                      >
                        {c.label}
                      </th>
                    ))}
                    {hasRowActions && (
                      <th className="p-3 text-center font-bold">
                        {t("team.actions")}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row, i) => (
                    <tr
                      key={row.id}
                      data-testid={`crud-row-${row.id}`}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      className={`${onRowClick ? "cursor-pointer hover:brightness-95 " : ""}${
                        isDarkMode
                          ? i % 2 === 0
                            ? "bg-[#232323] text-white"
                            : "bg-[#1C1C1C] text-white"
                          : i % 2 === 0
                          ? "bg-gray-50 text-gray-900"
                          : "bg-white text-gray-900"
                      }`}
                    >
                      {columns.map((c) => (
                        <td key={c.key} className={`p-2 ${cellAlign(c.align)}`}>
                          {c.render ? c.render(row) : String(row[c.key] ?? "")}
                        </td>
                      ))}
                      {hasRowActions && <td className="p-2">{rowActions(row)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {filtered.length > pageSize && (
          <div className="mt-3 flex items-center justify-center gap-3 text-sm">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={safePage === 0}
              className="rounded-lg px-3 py-1 disabled:opacity-40 hover:bg-primary-500/10"
            >
              <i className="ri-arrow-left-s-line" />
            </button>
            <span className={isDarkMode ? "text-gray-300" : "text-gray-700"}>
              {safePage + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={safePage >= totalPages - 1}
              className="rounded-lg px-3 py-1 disabled:opacity-40 hover:bg-primary-500/10"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        )}
        </div>
        </div>
      </div>

      {modal && (
        <Modal
          title={
            modal.mode === "edit"
              ? `${t("team.edit")} — ${title}`
              : `${t("team.new")} — ${title}`
          }
          onClose={closeModal}
          themeClasses={tc}
          content={
            <EntityForm
              fields={fields}
              initial={modal.row}
              themeClasses={tc}
              isDarkMode={isDarkMode}
              submitLabel={
                modal.mode === "edit" ? t("team.save") : t("team.create")
              }
              footer={formFooter}
              onCancel={closeModal}
              onSubmit={handleSubmit}
            />
          }
        />
      )}
    </div>
  );
}
